/**
 * Parse HSL color string to object
 * Handles: "hsl(350, 70%, 80%)" or "350 70% 80%"
 */
export const parseHSL = (hslString) => {
  // Match patterns like "hsl(350, 70%, 80%)" or "350 70% 80%"
  const match = hslString.match(/(\d+\.?\d*)[,\s]+(\d+\.?\d*)%?[,\s]+(\d+\.?\d*)%?/);
  
  if (!match) {
    return { h: 0, s: 50, l: 50 };
  }

  return {
    h: parseFloat(match[1]),
    s: parseFloat(match[2]),
    l: parseFloat(match[3])
  };
};

/**
 * Convert HSL object to CSS string
 */
export const hslToString = (hsl) => {
  return `hsl(${Math.round(hsl.h)}, ${Math.round(hsl.s)}%, ${Math.round(hsl.l)}%)`;
};

/**
 * Mix multiple HSL colors with weights
 * Uses weighted average for S and L, and circular average for H
 */
export const mixColors = (colorWeights) => {
  if (!colorWeights || colorWeights.length === 0) {
    return 'hsl(40, 60%, 70%)'; // Default amber
  }

  if (colorWeights.length === 1) {
    return colorWeights[0].color;
  }

  const totalWeight = colorWeights.reduce((sum, cw) => sum + cw.weight, 0);
  
  if (totalWeight === 0) {
    return colorWeights[0].color;
  }

  // Parse all colors
  const parsed = colorWeights.map(cw => ({
    ...parseHSL(cw.color),
    weight: cw.weight / totalWeight
  }));

  // Calculate weighted averages
  // For hue, we need to handle circular averaging
  let sinSum = 0;
  let cosSum = 0;
  let sSum = 0;
  let lSum = 0;

  parsed.forEach(({ h, s, l, weight }) => {
    // Convert hue to radians for circular average
    const hRad = (h * Math.PI) / 180;
    sinSum += Math.sin(hRad) * weight;
    cosSum += Math.cos(hRad) * weight;
    sSum += s * weight;
    lSum += l * weight;
  });

  // Calculate average hue from sin/cos components
  let avgHue = (Math.atan2(sinSum, cosSum) * 180) / Math.PI;
  if (avgHue < 0) avgHue += 360;

  // For saturation and lightness, slightly reduce to simulate pigment mixing
  // Real pigments tend to become more muted when mixed
  const mixFactor = Math.min(colorWeights.length * 0.05, 0.15);
  const avgSat = Math.max(sSum * (1 - mixFactor), 20);
  const avgLight = Math.max(Math.min(lSum * (1 - mixFactor * 0.5), 75), 30);

  return hslToString({
    h: avgHue,
    s: avgSat,
    l: avgLight
  });
};

/**
 * Generate a lighter version of a color (for highlights)
 */
export const lightenColor = (hslString, amount = 15) => {
  const hsl = parseHSL(hslString);
  return hslToString({
    h: hsl.h,
    s: Math.max(hsl.s - 10, 0),
    l: Math.min(hsl.l + amount, 95)
  });
};

/**
 * Generate a darker version of a color (for shadows)
 */
export const darkenColor = (hslString, amount = 15) => {
  const hsl = parseHSL(hslString);
  return hslToString({
    h: hsl.h,
    s: Math.min(hsl.s + 5, 100),
    l: Math.max(hsl.l - amount, 10)
  });
};

/**
 * Get a complementary accent color
 */
export const getComplementary = (hslString) => {
  const hsl = parseHSL(hslString);
  return hslToString({
    h: (hsl.h + 180) % 360,
    s: hsl.s,
    l: hsl.l
  });
};
